import { Direction } from '@angular/cdk/bidi';
import { DOCUMENT, isPlatformServer } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Inject,
  Input,
  OnDestroy,
  Output,
  PLATFORM_ID,
} from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import SwiperCore, { Keyboard, Navigation, Pagination } from 'swiper';
import { PlatformType } from '../../../common';
import { ClientConfigService, ImgQuery } from '../../services';
import { getImgUrlByPlatform } from '../../utils';
import { MarketingHeroViewModel } from './marketing-hero-view-model';

SwiperCore.use([Keyboard, Pagination, Navigation]);

interface ProcessedTile {
  imgQuery: ImgQuery;
  fallbackUrl: string;
  title: string;
  description: string;
}

@Component({
  selector: 'osn-shared-marketing-hero',
  templateUrl: './marketing-hero.component.html',
  styleUrls: ['./marketing-hero.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MarketingHeroComponent implements OnDestroy {
  @Input()
  public set vm(value: MarketingHeroViewModel) {
    this._vm = value;
    this.processedTiles = this.createProcessedTitles(value);
    this.initAutoSwiping();
    this.showNavigationArrows = this.processedTiles.length > 1;
  }
  public get vm(): MarketingHeroViewModel {
    return this._vm;
  }
  @Output() public buttonClick: EventEmitter<string> = new EventEmitter();
  public position = 0;
  public processedTiles: ProcessedTile[];
  public showNavigationArrows = false;

  private readonly HERO_SCROLL_TIMER = this.clientConfigService.marketingHeroTrayScrollPeriodMilliseconds;
  private timerHandle: ReturnType<typeof setTimeout>;
  private _vm: MarketingHeroViewModel;

  constructor(
    @Inject(DOCUMENT) private readonly document: Document,
    private readonly translateService: TranslateService,
    private readonly cdr: ChangeDetectorRef,
    private readonly clientConfigService: ClientConfigService,
    @Inject(PLATFORM_ID) private readonly platformId
  ) {}

  public get documentDir(): Direction {
    return this.document.dir === 'rtl' ? 'rtl' : 'ltr';
  }

  public get buttonText(): string {
    return this.vm?.button?.labelLocalized[this.translateService.currentLang];
  }

  public ngOnDestroy(): void {
    clearInterval(this.timerHandle);
  }

  public onSelectTile(newPosition: number): void {
    this.position = this.calculatePosition(newPosition);
    this.cdr.detectChanges();
    this.initAutoSwiping();
  }

  public onButtonClick(): void {
    this.buttonClick.emit(this.vm.button.labelLocalized.en);
  }

  private createProcessedTitles(vm: MarketingHeroViewModel): ProcessedTile[] {
    return vm.tiles.map((tile) => {
      const mobileImg = getImgUrlByPlatform(PlatformType.Mobile, tile.backgroundImageUrlsByPlatform);
      const tabletImg = getImgUrlByPlatform(PlatformType.Tablet, tile.backgroundImageUrlsByPlatform);
      const desktopImg = getImgUrlByPlatform(PlatformType.Web, tile.backgroundImageUrlsByPlatform);
      return {
        imgQuery: {
          XS: { url: mobileImg },
          SM: { url: tabletImg },
          MD: { url: desktopImg },
          LG: { url: desktopImg },
          XL: { url: desktopImg },
        },
        fallbackUrl: tile.fallbackBackgroundImageUrl,
        title: tile.title,
        description: tile.description,
      };
    });
  }

  private initAutoSwiping(): void {
    if (isPlatformServer(this.platformId)) {
      return;
    }
    clearInterval(this.timerHandle);
    this.timerHandle = setInterval(() => {
      if (this.position === this.processedTiles.length - 1) {
        this.position = 0;
      } else {
        this.position = this.position + 1;
      }
      this.cdr.detectChanges();
    }, this.HERO_SCROLL_TIMER);
  }

  private calculatePosition(newPosition: number): number {
    if (newPosition === this.processedTiles.length) {
      return 0;
    }
    if (newPosition === -1) {
      return this.processedTiles.length;
    }
    return newPosition;
  }
}
